<template>
  <div class="score-app" :class="{ 'score-app--embedded': isEmbedded }">
    <site-header v-if="!isEmbedded" :dark="showHome" :on-home="showHome" />

    <main class="score-main" :aria-busy="loading || loadingResult ? 'true' : 'false'">
      <home-page
        v-if="showHome"
        v-model="uri"
        :loading="loading"
        :error="uriError"
        :could-not-score="couldNotScore"
        :support-href="scoreSupportHref"
        @score="scoreUri"
      />

      <section v-else-if="showEmbeddedStart" class="score-column embed-start" aria-labelledby="embed-title">
        <h1 id="embed-title" class="embed-title">Score your form</h1>
        <url-box
          v-if="!immediateAnalysis"
          v-model="uri"
          input-id="embed-url"
          :loading="loading"
          :error="uriError"
          @submit="scoreUri"
        />
        <p v-if="loading && immediateAnalysis" class="score-status" role="status">
          <ph-icon name="spinner-gap" :size="20" class="score-spinner" />
          Scoring {{ uriWithoutPrefix }}
        </p>
        <p v-if="couldNotScore" class="score-help">
          Some forms only load after a click or a login. Still stuck?
          <a :href="scoreSupportHref">Let us know</a> and we'll look into it.
        </p>
      </section>

      <div v-else-if="errorLoadingResult" class="score-column">
        <section class="score-notice" aria-labelledby="notice-title">
          <h1 id="notice-title" class="score-notice-title">We couldn't find this score</h1>
          <p>Scores stay available for 90 days. <a href="/">Score the form again</a>, it takes a minute.</p>
          <p>Still think something's wrong? <a :href="resultSupportHref">Let us know</a>.</p>
        </section>
      </div>

      <div v-else-if="!hasResults" class="score-column">
        <p class="score-status" role="status">
          <ph-icon name="spinner-gap" :size="20" class="score-spinner" />
          Loading the score
        </p>
      </div>

      <embed-result
        v-else-if="isEmbedded"
        :score="scoreData.score"
        :details="details"
        :shown-details="shownDetails"
        :grade="grade"
        :counts="counts"
        :fixes="fixes"
        :full-report="fullReport"
        :improvements-only="improvementsOnly"
        :check-count="checkCount"
        :uri="uri"
        :share-uri="shareUri"
        :email-result-uri="emailResultUri"
        :public-json-uri="publicJsonUri"
        @toggle-improvements="toggleImprovementsOnly"
      />

      <template v-else>
        <result-hero
          :score="scoreData.score"
          :details="details"
          :grade="grade"
          :uri="uri"
          :share-uri="shareUri"
          :field-count="fieldCount"
          :scored-date="scoredDate"
          @restart="restart"
        />

        <div class="score-column score-sections">
          <template v-if="!fullReport">
            <email-unlock
              :score="scoreData.score"
              :check-count="checkCount"
              :uri="uri"
              :result-uri="emailResultUri"
              :public-json-uri="publicJsonUri"
            />
            <div class="score-promos">
              <free-plan-card :score="scoreData.score" />
              <chrome-extension-card />
            </div>
          </template>

          <template v-else>
            <fixes-first v-if="fixes.length" :fixes="fixes" />
            <div class="score-promos no-print">
              <free-plan-card :score="scoreData.score" />
              <chrome-extension-card />
            </div>
            <div class="score-checks">
              <mobile-note />
              <all-checks
                :details="shownDetails"
                :counts="counts"
                :improvements-only="improvementsOnly"
                @toggle-improvements="toggleImprovementsOnly"
              />
            </div>
            <form-details v-if="scoreData.form" :form="scoreData.form" :uri="uri" @download="downloadCsv" />
          </template>
        </div>
      </template>
    </main>

    <site-footer v-if="!isEmbedded" />
  </div>
</template>

<script>
import axios from 'axios'
import AllChecks from './AllChecks.vue'
import ChromeExtensionCard from './ChromeExtensionCard.vue'
import EmailUnlock from './EmailUnlock.vue'
import EmbedResult from './EmbedResult.vue'
import FixesFirst from './FixesFirst.vue'
import FormDetails from './FormDetails.vue'
import FreePlanCard from './FreePlanCard.vue'
import HomePage from './HomePage.vue'
import MobileNote from './MobileNote.vue'
import PhIcon from './PhIcon.vue'
import ResultHero from './ResultHero.vue'
import SiteFooter from './SiteFooter.vue'
import SiteHeader from './SiteHeader.vue'
import UrlBox from './UrlBox.vue'
import {
  AUDIT_CHECK_COUNT,
  buildCsv,
  countChecks,
  filterImprovements,
  formatScoredDate,
  getCsvFileName,
  getScoreGrade,
  hasChecks,
  isImprovementsQuery,
  parseScoredAt,
  rankFixes,
  withImprovementsQuery
} from '../score/report.mjs'
import { SCORES_URL } from '../score/links'
import { EMAIL_SOURCE_QUERY, EVENTS, errorStatus, trackEvent } from '../score/analytics'

const URI_ERRORS = Object.freeze({
  empty: 'Paste the address of the page the form is on',
  unreachable: "We couldn't open that page. Check that it's public and try again"
})

// The token that opens the full report, remembered per result so this browser keeps seeing it.
const UNLOCK_STORAGE_KEY = (hash) => `exatom-score-unlock:${hash}`

const readStoredToken = (hash) => {
  try {
    return localStorage.getItem(UNLOCK_STORAGE_KEY(hash))
  } catch (e) {
    return null
  }
}

const writeStoredToken = (hash, token) => {
  try {
    if (token) {
      localStorage.setItem(UNLOCK_STORAGE_KEY(hash), token)
    } else {
      localStorage.removeItem(UNLOCK_STORAGE_KEY(hash))
    }
  } catch (e) {
    // Storage is unavailable (private mode, blocked site data); the report link in the email still works.
  }
}

const fetchScoreFile = async (name) => (await axios.get(`${SCORES_URL}${encodeURIComponent(name)}.json`)).data

const mailto = (subject, body) => `mailto:support@exatom.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

export default {
  name: 'ScoreComponent',
  components: {
    AllChecks,
    ChromeExtensionCard,
    EmailUnlock,
    EmbedResult,
    FixesFirst,
    FormDetails,
    FreePlanCard,
    HomePage,
    MobileNote,
    PhIcon,
    ResultHero,
    SiteFooter,
    SiteHeader,
    UrlBox
  },
  data () {
    return {
      loading: false,
      loadingResult: false,
      uri: '',
      scoreData: {},
      couldNotScore: false,
      errorLoadingResult: false,
      uriError: '',
      improvementsOnly: isImprovementsQuery(this.$route.query),
      unlockToken: null,
      checkCount: AUDIT_CHECK_COUNT
    }
  },
  computed: {
    details () {
      return this.scoreData?.details ?? {}
    },
    hasResults () {
      return Object.keys(this.details).length > 0
    },
    fullReport () {
      return hasChecks(this.details)
    },
    grade () {
      return getScoreGrade(this.scoreData.score, this.details)
    },
    counts () {
      return countChecks(this.details)
    },
    fixes () {
      return rankFixes(this.details)
    },
    shownDetails () {
      return this.improvementsOnly ? filterImprovements(this.details) : this.details
    },
    fieldCount () {
      return this.scoreData.form?.elements?.length ?? 0
    },
    scoredDate () {
      return formatScoredDate(parseScoredAt(this.scoreData.ts))
    },
    uriWithoutPrefix () {
      return this.uri.replace(/^https?:\/\/(www\.)?/i, '')
    },
    isEmbedded () {
      return 'embed' in this.$route.query
    },
    isShareUri () {
      return typeof this.$route.params.hash !== 'undefined'
    },
    immediateAnalysis () {
      return !!this.$route.params.url
    },
    showHome () {
      return !this.isEmbedded && !this.isShareUri && !this.hasResults
    },
    showEmbeddedStart () {
      return this.isEmbedded && !this.isShareUri && !this.hasResults
    },
    resultHash () {
      return this.$route.params.hash ?? this.scoreData.ui?.split('/').pop()
    },
    publicJsonUri () {
      return `${SCORES_URL}${this.resultHash}.json`
    },
    resultUri () {
      return `${document.location.origin}/result/${this.resultHash}`
    },
    // The link in the email; the backend adds the token.
    emailResultUri () {
      return `${this.resultUri}?${EMAIL_SOURCE_QUERY.key}=${EMAIL_SOURCE_QUERY.value}`
    },
    // The full report when this browser has it, so the people it is shared with see the same.
    shareUri () {
      const url = new URL(this.resultUri)
      if (this.unlockToken) {
        url.searchParams.set('token', this.unlockToken)
      }
      return withImprovementsQuery(url.toString(), this.improvementsOnly)
    },
    scoreSupportHref () {
      return mailto('Exatom Score - unable to score form', `Hi!\r\n\r\nI was not able to score my form, could you have a look?\r\n${this.uri}`)
    },
    resultSupportHref () {
      return mailto('Exatom Score - unable to find form score results', `Hi!\r\n\r\nI was not able to retrieve my score results, could you have a look?\r\n${document.location.href}`)
    }
  },
  watch: {
    '$route.params.hash' (hash) {
      if (hash) this.loadResult()
    }
  },
  async mounted () {
    const background = this.$route.query.bg
    if (this.isEmbedded && background && (background === 'transparent' || background.startsWith('#'))) {
      document.body.style.background = background
      document.getElementById('scoreApp').style.background = background
    }
    this.$api.addTags()

    if (this.isShareUri) {
      await this.loadResult()
    }

    if (this.immediateAnalysis) {
      this.uri = this.$route.params.url
      this.scoreUri()
    }
  },
  methods: {
    async loadResult () {
      const hash = this.$route.params.hash
      const token = this.$route.query.token || readStoredToken(hash)
      this.loadingResult = true
      this.errorLoadingResult = false

      try {
        if (token) {
          try {
            this.scoreData = await fetchScoreFile(token)
            this.unlockToken = token
            writeStoredToken(hash, token)
          } catch (e) {
            // An expired or wrong token: show the score without the full report.
            writeStoredToken(hash, null)
            this.scoreData = await fetchScoreFile(hash)
          }
        } else {
          this.scoreData = await fetchScoreFile(hash)
        }
        this.uri = this.scoreData.uri ?? ''
        this.trackEmailVisit()
      } catch (e) {
        this.errorLoadingResult = true
      } finally {
        this.loadingResult = false
      }
    },
    // Counts a visit from the email link once, then drops the marker so shared links don't carry it.
    trackEmailVisit () {
      if (this.$route.query[EMAIL_SOURCE_QUERY.key] !== EMAIL_SOURCE_QUERY.value) return
      if (this.unlockToken) {
        trackEvent(EVENTS.fullReportFromEmail)
      }
      const url = new URL(document.location.href)
      url.searchParams.delete(EMAIL_SOURCE_QUERY.key)
      history.replaceState(history.state, '', url.toString())
    },
    restart () {
      this.scoreData = {}
      this.uri = ''
      this.uriError = ''
      this.couldNotScore = false
      this.unlockToken = null
      this.improvementsOnly = false

      if (this.$route.path === '/') {
        // Scoring replaced the address with the result URL without a route change.
        history.replaceState(history.state, '', '/')
      } else {
        this.$router.push('/')
      }
      window.scrollTo(0, 0)
    },
    urlFull (uri) {
      return /^https?:\/\//i.test(uri) ? uri : `https://${uri}`
    },
    scoreUri () {
      this.couldNotScore = false
      this.uriError = ''

      if (!this.uri) {
        this.uriError = URI_ERRORS.empty
        return
      }

      this.getScore(this.urlFull(this.uri))
    },
    async getScore (uri) {
      this.loading = true
      trackEvent(EVENTS.urlSubmitted, { domain: new URL(uri).hostname.replace(/^www\./i, '') })
      let scoring
      try {
        scoring = (await this.$api.assets('/score', { uri })).data
      } catch (e) {
        scoring = null
        trackEvent(EVENTS.assetGeneratorError, { endpoint: '/score', status: errorStatus(e) })
      } finally {
        this.loading = false
      }

      if (scoring && Object.values(scoring).length && scoring.details) {
        this.scoreData = scoring
        this.uri = uri
        this.$api.addTags('exatomConversion')

        if (scoring.ui) {
          history.replaceState(history.state, '', `${document.location.origin}/result/${scoring.ui.split('/').pop()}${this.isEmbedded ? document.location.search : ''}`)
        }
        window.scrollTo(0, 0)
      } else {
        if (scoring) {
          trackEvent(EVENTS.assetGeneratorError, { endpoint: '/score', status: 'no form found' })
        }
        this.couldNotScore = true
        this.uriError = URI_ERRORS.unreachable
      }
    },
    toggleImprovementsOnly () {
      this.improvementsOnly = !this.improvementsOnly
      history.replaceState(history.state, '', withImprovementsQuery(document.location.href, this.improvementsOnly))
    },
    downloadCsv () {
      const blob = new Blob([buildCsv(this.details)], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = getCsvFileName(this.uri, parseScoredAt(this.scoreData.ts) ?? new Date())
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.score-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--ex-color-white);
}

.score-app--embedded {
  min-height: 0;
  background: transparent;
}

.score-main {
  flex: 1;
}

.score-column {
  width: 1040px;
  max-width: 100%;
  margin: 0 auto;
  padding: 56px 0 88px;
}

.score-sections {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.score-checks {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.score-promos {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 24px;
}

.score-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--ex-color-neutral-700);
}

.score-notice {
  padding: 28px 32px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-panel);
  background: var(--ex-color-white);
  line-height: 1.55;
  color: var(--ex-color-neutral-700);
}

.score-notice p + p {
  margin-top: 8px;
}

.score-notice-title {
  margin-bottom: 12px;
  font-size: 28px;
  letter-spacing: -0.02em;
}

.embed-start {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 32px 16px;
  text-align: center;
}

.embed-start ::v-deep .url-box-field {
  border-color: var(--ex-color-neutral-100);
}

.embed-title {
  font-size: 36px;
  letter-spacing: -0.04em;
}

.score-help {
  max-width: 640px;
  font-size: 14px;
  color: var(--ex-color-neutral-700);
}

@media (prefers-reduced-motion: no-preference) {
  .score-spinner {
    animation: score-spin 1s linear infinite;
  }
}

@keyframes score-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1072px) {
  .score-column {
    padding: 40px 16px 64px;
  }
}

@media (max-width: 960px) {
  .score-promos {
    grid-template-columns: 1fr;
  }
}
</style>
